import * as React from 'react';

import { useFloatingHeader } from './useFloatingHeader';

const COLUMN_WIDTH = 316;
const COLUMN_GAP = 20;
const COLUMN_STRIDE = COLUMN_WIDTH + COLUMN_GAP;
// Порог смещения указателя (px), после которого нажатие считается перетаскиванием, а не кликом.
const DRAG_THRESHOLD = 5;

type TScrollerProps = Pick<
  React.HTMLAttributes<HTMLDivElement>,
  'onKeyDown' | 'onPointerDown' | 'onPointerMove' | 'onPointerUp' | 'onPointerCancel' | 'onScroll'
>;

interface IUseTableScrollResult {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  floatingHeaderScrollRef: React.RefObject<HTMLDivElement | null>;
  headerRowRef: React.RefObject<HTMLDivElement | null>;
  dragging: boolean;
  hasHorizontalOverflow: boolean;
  isFloatingHeaderMounted: boolean;
  isFloatingHeaderVisible: boolean;
  scrollByColumn(direction: 'left' | 'right'): void;
  scrollerProps: TScrollerProps;
}

export function useTableScroll(): IUseTableScrollResult {
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const floatingHeaderScrollRef = React.useRef<HTMLDivElement | null>(null);
  const headerRowRef = React.useRef<HTMLDivElement | null>(null);
  const dragStateRef = React.useRef({ armed: false, capturing: false, scrollLeft: 0, x: 0 });
  const [dragging, setDragging] = React.useState(false);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = React.useState(false);

  const updateHorizontalOverflow = React.useCallback(() => {
    const scrollElement = scrollRef.current;

    if (scrollElement) {
      setHasHorizontalOverflow(scrollElement.scrollWidth > scrollElement.clientWidth);
    }
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const scrollElement = scrollRef.current;

    if (!scrollElement) {
      return;
    }

    updateHorizontalOverflow();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateHorizontalOverflow);

      return () => {
        window.removeEventListener('resize', updateHorizontalOverflow);
      };
    }

    const resizeObserver = new ResizeObserver(updateHorizontalOverflow);
    resizeObserver.observe(scrollElement);

    const table = scrollElement.firstElementChild;
    if (table) {
      resizeObserver.observe(table);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateHorizontalOverflow]);

  const { isFloatingHeaderMounted, isFloatingHeaderVisible, syncFloatingHeaderScroll } = useFloatingHeader({
    floatingHeaderScrollRef,
    headerRowRef,
    scrollRef,
  });

  const scrollByColumn = (direction: 'left' | 'right') => {
    const scrollElement = scrollRef.current;

    if (!scrollElement) {
      return;
    }

    const { scrollLeft } = scrollElement;
    const target =
      direction === 'right'
        ? (Math.floor(scrollLeft / COLUMN_STRIDE) + 1) * COLUMN_STRIDE
        : Math.max(0, (Math.ceil(scrollLeft / COLUMN_STRIDE) - 1) * COLUMN_STRIDE);

    scrollElement.scrollTo({ behavior: 'smooth', left: target });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }

    event.preventDefault();
    scrollByColumn(event.key === 'ArrowLeft' ? 'left' : 'right');
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    syncFloatingHeaderScroll(event.currentTarget.scrollLeft);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !scrollRef.current) {
      return;
    }

    // Захват указателя откладываем до реального движения (см. handlePointerMove), чтобы одиночный клик
    // доходил до дочерних контролов и setPointerCapture не «крал» у них click.
    dragStateRef.current = {
      armed: true,
      capturing: false,
      scrollLeft: scrollRef.current.scrollLeft,
      x: event.clientX,
    };
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const dragState = dragStateRef.current;

    if (!dragState.armed || !scrollRef.current) {
      return;
    }

    const distance = event.clientX - dragState.x;

    if (!dragState.capturing) {
      if (Math.abs(distance) < DRAG_THRESHOLD) {
        return;
      }

      // Порог пройден — это drag, а не клик: включаем захват и режим перетаскивания.
      dragState.capturing = true;
      event.currentTarget.setPointerCapture(event.pointerId);
      setDragging(true);
    }

    scrollRef.current.scrollLeft = dragState.scrollLeft - distance;
    syncFloatingHeaderScroll(scrollRef.current.scrollLeft);
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    dragStateRef.current.armed = false;
    dragStateRef.current.capturing = false;
    setDragging(false);
  };

  return {
    scrollRef,
    floatingHeaderScrollRef,
    headerRowRef,
    dragging,
    hasHorizontalOverflow,
    isFloatingHeaderMounted,
    isFloatingHeaderVisible,
    scrollByColumn,
    scrollerProps: {
      onKeyDown: handleKeyDown,
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerEnd,
      onPointerCancel: handlePointerEnd,
      onScroll: handleScroll,
    },
  };
}
